import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import * as env from "../environments/config";
import {
  getLocalStorageUserSession as _getLocalStorageUserSession,
  clearLocalStorageUserSession as _clearLocalStorageUserSession,
} from "../services/secureStorage";
import { persistSessionFromLogin as _persistSessionFromLogin } from "./session";
import { logger } from "./logger";
import { authLogin } from "../services/login";

export type PublicUser = {
  id?: string;
  email: string;
  companyId: string;
  companyName?: string;
  companyEmail?: string;
} | null;

type AuthCtx = {
  user: PublicUser;
  isAuthenticated: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
};

export interface LoginApiSuccess {
  status: "SUCCESS";
  message: string;
  request_id: string;
  data: {
    auth: {
      idToken: string;
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
      tokenType: string; // "Bearer"
      publicKey: string;
      username: string;
      company: {
        photo: string | null;
        companyColor: string | null;
        textColor: string | null;
        companyEmail: string | null;
        id_company: number;
        company_name: string;
      };
    };
  };
}

const isFn = (v: unknown): v is (...a: any[]) => any => typeof v === "function";

function safeParseBackendError(err: any): string {
  try {
    const code = err?.response?.data?.error?.code;
    const reason = err?.response?.data?.reason;
    const details = err?.response?.data?.details;
    const detailsText = Array.isArray(details)
      ? details.join(" ").trim()
      : typeof details === "string"
        ? details.trim()
        : undefined;
    return code ?? detailsText ?? reason ?? err?.message ?? "Algo salió mal.";
  } catch {
    return "Algo salió mal.";
  }
}

function toPublicUserLike(d: any): NonNullable<PublicUser> {
  const comp = d?.company ?? {};
  return {
    id: String(d?.id ?? d?.email ?? ""),
    email: String(d?.email ?? ""),
    companyId: String(comp?.id ?? comp?.id_company ?? ""),
    companyName: comp?.name ?? comp?.company_name ?? undefined,
    companyEmail: comp?.email ?? comp?.companyEmail ?? undefined,
  };
}

const Ctx = createContext<AuthCtx>(null as any);
export const useAuth = () => useContext(Ctx);

const cfg = process.env.REACT_APP_ENV ? ((env as any)[process.env.REACT_APP_ENV] ?? {}) : {};
const { API_LOGIN } = (cfg || {}) as { API_LOGIN?: string };

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<PublicUser>(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const getLocalStorageUserSession = isFn(_getLocalStorageUserSession)
    ? _getLocalStorageUserSession
    : ((async () => null) as typeof _getLocalStorageUserSession);

  const clearLocalStorageUserSession = isFn(_clearLocalStorageUserSession)
    ? _clearLocalStorageUserSession
    : () => {};

  const persistSessionFromLogin = isFn(_persistSessionFromLogin)
    ? _persistSessionFromLogin
    : ((async () => {
        throw new Error("persistSessionFromLogin no es función");
      }) as typeof _persistSessionFromLogin);

  useEffect(() => {
    mountedRef.current = true;

    (async () => {
      try {
        const decrypted = await getLocalStorageUserSession<any>();
        const d = decrypted?.data;
        if (!d) return;

        const t = d?.tokens ?? d?.auth ?? {};
        const accessToken = t?.access_token ?? undefined;
        const tokenType = t?.token_type ?? t?.tokenType ?? "Bearer";

        if (accessToken) {
          axios.defaults.headers.common["Authorization"] = `${tokenType} ${accessToken}`;
        }

        const publicUser = toPublicUserLike(d);
        if (mountedRef.current) setUser(publicUser);
      } catch (e) {
        logger?.error?.("Auth restore error:", e);
        try {
          clearLocalStorageUserSession();
        } catch {
          // eslint-disable-next-line no-console
          console.warn("Auth.restore: clearLocalStorageUserSession falló");
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const login = async (username: string, password: string) => {
    if (!API_LOGIN) {
      const errMsg = "API_LOGIN no está configurado.";
      logger?.error?.("LOGIN_ERROR:", errMsg);
      throw new Error(errMsg);
    }

    try {
      const resp = await authLogin({ username, password });
      const session = await persistSessionFromLogin(resp);

      const t = session?.data?.tokens ?? {};
      const accessToken = t?.access_token ?? undefined;
      const tokenType = t?.token_type ?? "Bearer";

      if (accessToken) {
        axios.defaults.headers.common["Authorization"] = `${tokenType} ${accessToken}`;
      }

      const publicUser = toPublicUserLike(session?.data);
      if (mountedRef.current) setUser(publicUser);
    } catch (err: any) {
      const backendMsg = safeParseBackendError(err) || "No se pudo iniciar sesión.";
      logger?.error?.("LOGIN_ERROR:", backendMsg, err);
      throw new Error(backendMsg);
    }
  };

  const logout = () => {
    try {
      setUser(null);
    } catch {
      // eslint-disable-next-line no-console
      console.warn("Auth.logout: setUser falló");
    }
    try {
      clearLocalStorageUserSession();
    } catch {
      // eslint-disable-next-line no-console
      console.warn("Auth.logout: setUser falló");
    }
    try {
      delete axios.defaults.headers.common["Authorization"];
    } catch {
      // eslint-disable-next-line no-console
      console.warn("Auth.logout: setUser falló");
    }
  };

  const value = useMemo<AuthCtx>(
    () => ({
      user,
      isAuthenticated: !!user,
      loading,
      login,
      logout,
    }),
    [user, loading]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
